/*
 * ******************************************************************
 * Copyright (c) 2019-2024 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { Injectable } from "@angular/core";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { filter, map } from "rxjs/operators";
import { ErrorOccurredEvent, Event, MaintenanceModeEvent, OperationCompletedEvent, OperationInitEvent, RefreshEvent, TitleUpdateEvent } from "@app/services/dispatcher/events";
import { OverageEvent } from "@app/services/dispatcher/events/overage-event";

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class Dispatcher {
    public onRefresh: Observable<RefreshEvent>;
    public onOperationInit: Observable<OperationInitEvent>;
    public onOperationCompleted: Observable<OperationCompletedEvent>;
    public onError: Observable<ErrorOccurredEvent>;
    public onTitleUpdate: Observable<TitleUpdateEvent>;
    public onMaintenanceMode: Observable<MaintenanceModeEvent>;
    public onOverageStatus: Observable<OverageEvent>;
    public onAnyEvent: Observable<Event>;
    private onEvent: Subject<Event> = new Subject<Event>();
    private eventBus: Observable<Event> = this.onEvent.asObservable();
    private onMaintenanceModeEvent: ReplaySubject<MaintenanceModeEvent> = new ReplaySubject<MaintenanceModeEvent>(1);
    private maintenanceModeEventBus: Observable<MaintenanceModeEvent> = this.onMaintenanceModeEvent.asObservable();

    constructor() {
        this.onRefresh = this.eventBus.pipe(filter(event => event.id == RefreshEvent.ID),
            map(event => <RefreshEvent>event));
        this.onOperationInit = this.eventBus.pipe(filter(event => (event.id == OperationInitEvent.ID)),
            map(event => <OperationInitEvent>event));
        this.onOperationCompleted = this.eventBus.pipe(filter(event => (event.id == OperationCompletedEvent.ID)),
            map(event => <OperationCompletedEvent>event));
        this.onError = this.eventBus.pipe(filter(event => (event.id == ErrorOccurredEvent.ID)),
            map(event => <ErrorOccurredEvent>event));
        this.onTitleUpdate = this.eventBus.pipe(filter(event => (event.id == TitleUpdateEvent.ID)),
            map(event => <TitleUpdateEvent>event));
        this.onMaintenanceMode = this.maintenanceModeEventBus.pipe(map(event => <MaintenanceModeEvent>event));
        this.onOverageStatus = this.eventBus.pipe(filter(event => (event.id == OverageEvent.ID)),
            map(event => <OverageEvent>event));
        this.onAnyEvent = this.eventBus.pipe(map(event => event));
    }

    public publishEvent(event: Event): void {
        if (event.id === MaintenanceModeEvent.ID) {
            this.publishMaintenanceEvent(event as MaintenanceModeEvent);
        }
        this.onEvent.next(event);
    }

    public publishMaintenanceEvent(event: MaintenanceModeEvent): void {
        this.onMaintenanceModeEvent.next(event);
    }
}
